/**
 * focusLog — local mirror of completed focus work (Pomodoro sessions + schedule
 * blocks), so "Today's Schedule → Focus log" always has something to show even
 * before/without a successful round-trip to `/api/focus-sessions`.
 *
 * The backend remains the source of truth; this store exists so a just-finished
 * session appears instantly and survives an offline/failed POST. Entries from
 * both sources are merged and de-duplicated by `dedupeKey`.
 */

const STORE_KEY = 'fpFocusLog';
const KEEP_DAYS = 7;

export const FOCUS_LOG_EVENT = 'fp:focusLogUpdated';

/* local YYYY-MM-DD — avoids UTC-offset mismatches */
export const localDate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* Backend datetimes come back naive (UTC) — mark them so Date() doesn't read
 * them as local time and shift the entry by the timezone offset. */
export const parseServerDate = (v) => {
  if (!v) return null;
  const s = typeof v === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? `${v}Z` : v;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/* Two records describe the same session if they finished the same minute with
 * the same title + length — enough to collapse a server row onto its local twin. */
export const dedupeKey = (e) =>
  `${e.kind}|${(e.title || '').trim().toLowerCase()}|${e.minutes}|${String(e.at).slice(0, 16)}`;

const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
};

const write = (entries) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(entries)); } catch { /* quota — ignore */ }
};

/**
 * Append a completed unit of focus work.
 * @param {{kind:'block'|'session', title:string, minutes:number, type?:string,
 *          area?:string, plannedMin?:number, endedEarly?:boolean, at?:string}} entry
 */
export const appendFocusLog = (entry) => {
  const at = entry.at || new Date().toISOString();
  const record = {
    id: `fl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    kind: entry.kind || 'session',
    title: entry.title || 'Focus session',
    type: entry.type || null,
    area: entry.area || null,
    minutes: Math.max(0, Math.round(entry.minutes || 0)),
    plannedMin: entry.plannedMin != null ? Math.round(entry.plannedMin) : null,
    endedEarly: !!entry.endedEarly,
    at,
    source: 'local',
  };

  const cutoff = Date.now() - KEEP_DAYS * 24 * 60 * 60 * 1000;
  const kept = read().filter(e => {
    const t = new Date(e.at).getTime();
    return !isNaN(t) && t >= cutoff;
  });

  write([...kept, record]);
  try { window.dispatchEvent(new CustomEvent(FOCUS_LOG_EVENT, { detail: record })); } catch { /* SSR */ }
  return record;
};

/** Locally-mirrored entries from the last N local days (default 7). */
export const getLocalFocusLog = (days = KEEP_DAYS) => {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  return read().filter(e => {
    const t = new Date(e.at).getTime();
    return !isNaN(t) && t >= cutoff.getTime();
  });
};

/** True if `iso` falls within the last N local days. */
export const withinDays = (iso, days = KEEP_DAYS) => {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  const t = new Date(iso).getTime();
  return !isNaN(t) && t >= cutoff.getTime();
};

/** Normalise a `/api/focus-sessions` row into the shared entry shape. */
export const fromServerSession = (s) => {
  const at = parseServerDate(s.completed_at || s.created_at);
  if (!at) return null;
  return {
    id: s.id || `srv_${at.getTime()}`,
    kind: s.session_type === 'time_block' ? 'block' : 'session',
    title: s.task_name || 'Focus session',
    type: null,
    area: null,
    minutes: Math.max(0, Math.round(s.duration || 0)),
    plannedMin: null,
    endedEarly: false,
    at: at.toISOString(),
    source: 'server',
  };
};

/** Merge server + local entries, newest first, de-duplicated. */
export const mergeFocusEntries = (serverEntries = [], localEntries = []) => {
  const byKey = new Map();
  /* server first so its ids win when a pair collapses — but the server model
   * has no type/area, so fill those from the local twin instead of losing them */
  for (const e of [...serverEntries, ...localEntries]) {
    if (!e) continue;
    const k = dedupeKey(e);
    const kept = byKey.get(k);
    if (!kept) { byKey.set(k, e); continue; }
    byKey.set(k, {
      ...kept,
      type: kept.type || e.type,
      area: kept.area || e.area,
      plannedMin: kept.plannedMin != null ? kept.plannedMin : e.plannedMin,
      endedEarly: kept.endedEarly || e.endedEarly,
    });
  }
  return [...byKey.values()].sort((a, b) => new Date(b.at) - new Date(a.at));
};
